/**
 * Firebase Firestore Product Service
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import type { ProductConfig } from '../types';

// Collection reference
const productsCollection = collection(db, 'products');

// Product document type (with Firestore metadata)
export interface ProductDocument extends ProductConfig {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Get all products
 */
export async function getAllProducts(): Promise<ProductDocument[]> {
  const q = query(productsCollection, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  } as ProductDocument));
}

/**
 * Get a single product by ID
 */
export async function getProduct(id: string): Promise<ProductDocument | null> {
  const docRef = doc(productsCollection, id);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as ProductDocument;
}

/**
 * Check if a product ID already exists
 */
export async function productExists(id: string): Promise<boolean> {
  const docRef = doc(productsCollection, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists();
}

/**
 * Create a new product with custom ID
 */
export async function createProduct(id: string, product: ProductConfig): Promise<string> {
  const now = Timestamp.now();
  const docRef = doc(productsCollection, id);
  
  await setDoc(docRef, {
    ...product,
    createdAt: now,
    updatedAt: now,
  });
  
  return id;
}

/**
 * Update an existing product
 */
export async function updateProduct(id: string, product: Partial<ProductConfig>): Promise<void> {
  const docRef = doc(productsCollection, id);
  
  await updateDoc(docRef, {
    ...product,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a product
 */
export async function deleteProduct(id: string): Promise<void> {
  const docRef = doc(productsCollection, id);
  await deleteDoc(docRef);
}

/**
 * Delete specific fields from a product
 */
export async function deleteProductFields(id: string, fields: string[]): Promise<void> {
  const docRef = doc(productsCollection, id);
  const updates: Record<string, ReturnType<typeof deleteField>> = {};
  fields.forEach(field => {
    updates[field] = deleteField();
  });
  await updateDoc(docRef, updates);
}

/**
 * Slugify text for URL
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Generate product URL
 */
export function getProductUrl(product: ProductDocument): string {
  return `/urun/${product.id}`;
}
